// Gateway client — same-origin, httpOnly cookie session + double-submit CSRF.
// Mirrors symone-v2-admin's apiFetch, with two deliberate differences:
//   1. Every path is prefixed with API_BASE (default `/api`) so SPA routes such as
//      /triggers never collide with gateway paths. The dev proxy / load-balancer
//      route rule strips the prefix.
//   2. Refresh-on-401 applies to GETs only (per the dashboard brief). A mutation
//      that 401s is surfaced, never silently replayed.

// RFC 9457 Problem Detail, flattened. Gateway route errors arrive as
// `{"detail": <problem object | string>, "code": <code>}`; middleware auth errors as a
// bare problem object with `code`. Either way the top-level `code` is the stable one.
export interface ApiError {
  type: string
  title: string
  status: number
  detail: string | Record<string, unknown>
  remedy?: string
  /**
   * Stable error code (`forbidden`, `connector_denied`, `not_found`, `rate_limited`,
   * `quota_exceeded`, `conflict`, `connection_required`, …), read straight from the
   * body. Absent only when the body carried none (non-JSON / network-level failures).
   */
  code?: string
  /** POST /servers/{id}/activate 422: the required secret names still missing. */
  missing_secrets?: string[]
  /** GET /tools/{tool_name}/schema 404/424: the meta-tool's per-name code. */
  schema_error?: string
  /** 429 from /auth/signup and /auth/forgot-password: seconds until the IP may retry. */
  retry_after_seconds?: number
  /**
   * 403 `plan_restricted`: the entitlement key from the `X-Entitlement-Denied` response
   * header (e.g. `webhooks.config`, `triggers.max`). Present only on plan denials.
   */
  entitlement_denied?: string
}

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api'

export function apiUrl(path: string): string {
  return API_BASE + path
}

// Read the symone_csrf cookie (JS-readable, httponly=false)
export function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)symone_csrf=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

// Module-level single-flight: concurrent 401s share one in-flight refresh.
let refreshPromise: Promise<void> | null = null

// Bare fetch (not apiFetch) so a refresh can never recursively trigger refresh.
async function doRefresh(): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const csrf = getCsrfToken()
  if (csrf) headers['X-CSRF-Token'] = csrf
  const res = await fetch(apiUrl('/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
    headers,
  })
  if (!res.ok) throw new Error('Refresh failed')
}

function ensureRefresh(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

type ErrorBody = Partial<Omit<ApiError, 'detail'>> & { detail?: unknown }

function isProblem(v: unknown): v is ErrorBody {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && ('title' in v || 'type' in v)
}

function toApiError(res: Response, body: ErrorBody): ApiError {
  // `{detail: {type, title, status, detail, …}, code}` → lift the nested problem.
  const p: ErrorBody = isProblem(body.detail) ? body.detail : body
  const detail = p === body ? body.detail : p.detail
  const code = typeof body.code === 'string' ? body.code : typeof p.code === 'string' ? p.code : undefined
  const denied = res.headers?.get('X-Entitlement-Denied')
  return {
    type: p.type ?? 'about:blank',
    title: p.title ?? res.statusText,
    status: res.status,
    detail:
      typeof detail === 'string' || (typeof detail === 'object' && detail !== null)
        ? (detail as ApiError['detail'])
        : res.statusText,
    ...(typeof p.remedy === 'string' && p.remedy ? { remedy: p.remedy } : {}),
    ...(code ? { code } : {}),
    ...(Array.isArray(p.missing_secrets) ? { missing_secrets: p.missing_secrets } : {}),
    ...(typeof p.schema_error === 'string' ? { schema_error: p.schema_error } : {}),
    ...(typeof p.retry_after_seconds === 'number' ? { retry_after_seconds: p.retry_after_seconds } : {}),
    ...(denied ? { entitlement_denied: denied } : {}),
  }
}

async function throwFromResponse(res: Response): Promise<never> {
  let body: ErrorBody = {}
  try {
    const parsed: unknown = await res.json()
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) body = parsed as ErrorBody
  } catch {
    // Non-JSON error body — use defaults
  }
  throw toApiError(res, body)
}

export function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && typeof (err as ApiError).status === 'number'
}

// `retried=true` is the loop guard: the retry skips refresh, so a second
// consecutive 401 surfaces as an auth failure instead of looping.
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)

  if (init.body !== undefined) headers.set('Content-Type', 'application/json')
  if (MUTATION_METHODS.has(method)) {
    const csrf = getCsrfToken()
    if (csrf) headers.set('X-CSRF-Token', csrf)
  }

  const res = await fetch(apiUrl(path), { ...init, method, headers, credentials: 'include' })

  if (res.status === 401 && method === 'GET' && !retried) {
    try {
      await ensureRefresh()
    } catch {
      return throwFromResponse(res)
    }
    return apiFetch<T>(path, init, true)
  }

  if (!res.ok) return throwFromResponse(res)
  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}
